import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, forkJoin, of, Subject, switchMap } from 'rxjs';
import { SharedService } from '../../../../shared.service';
import { DroForm, DroPreviousValues, DroReading, DroReadingValidation, DroTabState } from '../../model/dro.model';
import { DroService } from '../../services/dro.service';
import { formatDroDate } from '../../utils/dro-validation';
import { DroCapturedValues, DroCaptureCardComponent } from '../dro-capture-card/dro-capture-card.component';
import { DroReadingHistoryComponent } from '../dro-reading-history/dro-reading-history.component';

type DroBannerTone = 'neutral' | 'ok' | 'error' | 'saved';

/**
 * DRO tab body shared by the Equipment popups (Sales and Service) and the Service Order popup: status banner,
 * capture card and reading history for one serial number. The host decides whether the tab exists at all.
 */
@Component({
  selector: 'lib-dro-tab',
  templateUrl: './dro-tab.component.html',
  styleUrl: './dro-tab.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, DroCaptureCardComponent, DroReadingHistoryComponent]
})
export class DroTabComponent implements OnInit, OnChanges {
  @Input() serialNumber = '';
  @Input() itemCode = '';
  /** Null on the equipment record: readings taken there are standalone. */
  @Input() serviceOrderId: string | null = null;
  /** The host's own rule (the service order status); the server's canCreate applies on top of it. */
  @Input() canCreate = true;
  @Input() readOnlyReason = '';
  @Input() restMessage = 'Recorded here when there is no service order — a consumables visit, a sales visit, or a number the customer reported.';
  /** True while the tab is the visible one; the history grid is first rendered then so Syncfusion can size it. */
  @Input() active = false;

  @Output() stateChange = new EventEmitter<DroTabState>();
  @Output() serviceOrderClick = new EventEmitter<string>();

  form: DroForm | null = null;
  previous: DroPreviousValues = {};
  readings: DroReading[] = [];
  /** The serial's reading for today, as decided by the server; it blocks another one until tomorrow. */
  todayReadingId: string | null = null;
  serverAllowsCreate = true;
  loading = false;
  loadFailed = false;
  capturing = false;
  saving = false;
  justSavedId: string | null = null;
  validation: DroReadingValidation | null = null;
  historyRendered = false;
  recorderName: string;

  private reload$ = new Subject<void>();

  constructor(
    private droService: DroService,
    private sharedService: SharedService,
    private destroyRef: DestroyRef
  ) {
    this.recorderName = this.droService.getRecorderName();
  }

  ngOnInit(): void {
    this.reload$.pipe(
      switchMap(() => {
        this.loading = true;
        this.loadFailed = false;
        return forkJoin([
          this.droService.getReadingContext(this.serialNumber, this.itemCode, this.serviceOrderId),
          this.droService.getReadingsBySerial(this.serialNumber, this.itemCode)
        ]).pipe(catchError(() => of(null))); // keep the reload stream alive after a failed request
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(responses => {
      this.loading = false;
      const [contextRes, readingsRes] = responses ?? [];
      if (!contextRes?.isSuccessful || !contextRes.result || !readingsRes?.isSuccessful) {
        this.loadFailed = true;
        this.sharedService.showNotification('error', contextRes?.message || readingsRes?.message || 'Failed to load DRO readings');
        return;
      }
      this.form = contextRes.result.form;
      this.previous = contextRes.result.previous;
      this.todayReadingId = contextRes.result.todayReadingId;
      this.serverAllowsCreate = contextRes.result.canCreate !== false;
      this.readings = readingsRes.result ?? [];
      if (this.todayReadingId || !this.form) {
        this.capturing = false;
      }
      this.emitState();
    });
    this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.active) {
      this.historyRendered = true;
    }
    if ((changes['serialNumber'] || changes['itemCode']) && !changes['serialNumber']?.firstChange) {
      this.capturing = false;
      this.justSavedId = null;
      this.load();
    }
  }

  load(): void {
    if (this.serialNumber && this.itemCode) {
      this.reload$.next();
    }
  }

  /** A new reading needs both the host's rule and the server's (the server knows the service order status too). */
  get canRecord(): boolean {
    return this.canCreate && this.serverAllowsCreate;
  }

  get tone(): DroBannerTone {
    if (this.loadFailed || (this.capturing && this.validation && !this.validation.valid)) {
      return 'error';
    }
    if (this.capturing) {
      return 'ok';
    }
    return this.todayReadingId ? 'saved' : 'neutral';
  }

  get summary(): string {
    if (!this.form) {
      return '';
    }
    const last = this.readings[0] ? ` · last reading ${formatDroDate(this.readings[0].readingDate)}` : '';
    return `${this.form.name} · ${this.form.fields.length} fields${last}`;
  }

  startReading(): void {
    this.validation = null;
    this.capturing = true;
    this.historyRendered = true;
  }

  cancelReading(): void {
    this.capturing = false;
    this.validation = null;
  }

  onValidationChange(validation: DroReadingValidation): void {
    this.validation = validation;
  }

  onSubmitReading(values: DroCapturedValues): void {
    if (!this.form?.formId) {
      return;
    }
    this.saving = true;
    this.droService.saveReading({
      serialNumber: this.serialNumber,
      itemCode: this.itemCode,
      formId: this.form.formId,
      serviceOrderId: this.serviceOrderId,
      values
    }).subscribe({
      next: res => {
        this.saving = false;
        if (res.isSuccessful) {
          this.justSavedId = res.result?.readingId ?? null;
          this.capturing = false;
        } else {
          this.sharedService.showNotification('error', res.message || 'Failed to save the reading');
        }
        this.load(); // also picks up a reading another user recorded first
      },
      error: () => {
        this.saving = false;
        this.sharedService.showNotification('error', 'Failed to save the reading');
      }
    });
  }

  onReadingDeleted(readingId: string): void {
    if (this.justSavedId === readingId) {
      this.justSavedId = null;
    }
    this.load();
  }

  private emitState(): void {
    this.stateChange.emit({
      readingCount: this.readings.length,
      todayReadingId: this.todayReadingId,
      serviceOrderReadingId: this.serviceOrderId
        ? this.readings.find(r => r.serviceOrderId === this.serviceOrderId)?.readingId ?? null
        : null
    });
  }
}
